import { createClient } from '@supabase/supabase-js';

// Define types for our blog posts
export type Post = {
  id: number;
  title: string;
  slug: string;
  content: string;
  thumbnail: string;
  created_at: string;
  updated_at: string;
  author_id: string;
  excerpt?: string;
  view_count?: number;
};

// Use the values from the Supabase integration client instead of env variables
// This ensures we always have valid values
const supabaseUrl = "https://fsyypxpydalatnrvyxqv.supabase.co";
const supabaseAnonKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZzeXlweHB5ZGFsYXRucnZ5eHF2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDYyNTY1OTgsImV4cCI6MjA2MTgzMjU5OH0._t7nMRRYDvWRt-962V4VyyOeI9nabjQrlncChOtS7cI";

// Create Supabase client with proper session handling
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storage: localStorage,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

// Blog post related functions
export async function getPosts() {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching posts:', error);
    return [];
  }
  
  return data as Post[];
}

export async function getPostBySlug(slug: string) {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .eq('slug', slug)
    .single();
  
  if (error) {
    console.error('Error fetching post by slug:', error);
    return null;
  }
  
  return data as Post;
}

export async function incrementPostViewCount(id: number) {
  const { data, error } = await supabase.rpc('increment_view_count', { post_id: id });
  
  if (error) {
    console.error('Error incrementing view count:', error);
    return false;
  }
  
  return true;
}

export async function getFeaturedPosts(limit = 3) {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .order('view_count', { ascending: false })
    .limit(limit);
  
  if (error) {
    console.error('Error fetching featured posts:', error);
    return [];
  }
  
  return data as Post[];
}

// Enhanced search function for better security
export async function searchPosts(query: string) {
  // Sanitize the search query to prevent injection attacks
  const sanitizedQuery = query.replace(/[^\w\s]/gi, '').trim();
  
  // Return empty array for empty queries or very short queries
  if (!sanitizedQuery || sanitizedQuery.length < 2) return [];
  
  // Process the query to extract meaningful terms
  const searchTerms = sanitizedQuery
    .toLowerCase()
    .split(/\s+/)
    .filter(term => term.length >= 2)
    .map(term => term.trim() + ':*'); // Add prefix matching
  
  if (searchTerms.length === 0) return [];
  
  // Join search terms with OR for more inclusive results
  const searchQuery = searchTerms.join(' | ');
  
  try {
    console.log('Searching with terms:', searchQuery);
    
    // Use enhanced full-text search with weighted columns
    const { data: textSearchResults, error: textSearchError } = await supabase
      .from('posts')
      .select('*')
      .textSearch('title', searchQuery, { 
        config: 'english',
        type: 'websearch'  // Use websearch parsing for better results
      })
      .order('view_count', { ascending: false })
      .limit(12);
    
    // If we get results from the title search, use those
    if (!textSearchError && textSearchResults?.length > 0) {
      console.log('Found results in title search:', textSearchResults.length);
      return textSearchResults as Post[];
    }
    
    // Try searching in content and excerpt as well
    const { data: contentSearchResults, error: contentSearchError } = await supabase
      .from('posts')
      .select('*')
      .textSearch('content', searchQuery, { 
        config: 'english',
        type: 'websearch'
      })
      .order('view_count', { ascending: false })
      .limit(12);
    
    if (!contentSearchError && contentSearchResults?.length > 0) {
      console.log('Found results in content search:', contentSearchResults.length);
      return contentSearchResults as Post[];
    }
    
    // Fallback to ILIKE for better matching on partial words
    console.log('Falling back to ILIKE search');
    const { data, error } = await supabase
      .from('posts')
      .select('*')
      .or(`title.ilike.%${sanitizedQuery}%,content.ilike.%${sanitizedQuery}%,excerpt.ilike.%${sanitizedQuery}%`)
      .order('view_count', { ascending: false })
      .limit(12);
    
    if (error) {
      console.error('Error searching posts:', error);
      return [];
    }
    
    return data as Post[];
  } catch (err) {
    console.error('Exception during search:', err);
    
    // Ultimate fallback to basic ILIKE search on just title
    const { data, error } = await supabase
      .from('posts')
      .select('*')
      .ilike('title', `%${sanitizedQuery}%`)
      .order('created_at', { ascending: false })
      .limit(8);
    
    if (error) {
      console.error('Error in fallback search:', error);
      return [];
    }
    
    return data as Post[];
  }
}

export async function getPostById(id: number) {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) {
    console.error('Error fetching post by id:', error);
    return null;
  }
  
  return data as Post;
}

export async function createPost(post: Omit<Post, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('posts')
    .insert([
      {
        ...post,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ])
    .select()
    .single();
  
  if (error) {
    console.error('Error creating post:', error);
    return null;
  }
  
  // Trigger notification to subscribers
  try {
    await supabase.functions.invoke('notify-subscribers', {
      body: { 
        post_id: data.id,
        title: data.title,
        slug: data.slug,
        excerpt: data.excerpt
      }
    });
  } catch (notifyError) {
    console.error('Error notifying subscribers:', notifyError);
    // Still return the post even if notification fails
  }
  
  return data as Post;
}

export async function updatePost(id: number, post: Partial<Post>) {
  const { data, error } = await supabase
    .from('posts')
    .update({
      ...post,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    console.error('Error updating post:', error);
    return null;
  }
  
  return data as Post;
}

export async function deletePost(id: number) {
  const { error } = await supabase
    .from('posts')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('Error deleting post:', error);
    return false;
  }
  
  return true;
}
